import { events } from "@constants/tracking";
import {
	getModuleLabel,
	getProethicaModuleListProps,
} from "@features/dashboard/helpers/proethica-module-list";
import { useSession } from "@features/session";
import useAnalytics from "@hooks/useAnalytics";
import useMixedItemDist from "@hooks/useMixedItemDist";
import type { MixedItemTag } from "@memorang/types";
import { useRouter } from "expo-router";
import type { Action } from "kbar";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { EXAM_STATUS } from "types/tag";

const statusIconMap: Record<EXAM_STATUS, string> = {
	NOT_STARTED: "play-circle-outline",
	ACTIVE: "play-pause",
	COMPLETE: "file-chart-outline",
	LEGACYREPORT: "file-chart-outline",
	MIGRATEDREPORT: "file-chart-outline",
};

const getModuleTags = (tags: MixedItemTag[] | undefined) =>
	(tags ?? []).filter(
		(tag) => tag.displayType?.includes("Display") && "distribution" in tag,
	);

export const useProethicaCommandBarActions = () => {
	const router = useRouter();
	const { t } = useTranslation();
	const { data: mixedItemDist } = useMixedItemDist();
	const { handleDirectSessionStart } = useSession();
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const moduleTags = useMemo(
		() => getModuleTags(mixedItemDist?.tags),
		[mixedItemDist?.tags],
	);

	return useMemo(() => {
		const trackOption = (option: string) => {
			explicitCallTrackCustomEvent({
				eventName: events.commandBarOptionClicked,
				option,
			});
		};

		const suggested: Action[] = [];
		const activeModule = moduleTags.find(
			(tag) => getProethicaModuleListProps(tag, t).status === "ACTIVE",
		);
		const nextNotStarted = moduleTags.find(
			(tag) => getProethicaModuleListProps(tag, t).status === "NOT_STARTED",
		);

		if (activeModule) {
			const module = getProethicaModuleListProps(activeModule, t);
			suggested.push({
				id: `resume-${module.id}`,
				name: t("commandBar.actions.resumeModule", { module: module.title }),
				subtitle: `${module.typeLabel} · ${module.durationLabel}`,
				section: t("commandBar.sections.suggested"),
				icon: "play-pause",
				keywords: `resume ${module.title} ${module.subtitle}`,
				perform: () => {
					trackOption(`resume-${module.id}`);
					handleDirectSessionStart({ push: true, moduleId: module.id });
				},
			});
		}

		if (
			nextNotStarted &&
			(!activeModule ||
				getModuleLabel(nextNotStarted) !== getModuleLabel(activeModule))
		) {
			const module = getProethicaModuleListProps(nextNotStarted, t);
			suggested.push({
				id: `start-${module.id}`,
				name: t("commandBar.actions.startModule", { module: module.title }),
				subtitle: module.subtitle,
				section: t("commandBar.sections.suggested"),
				icon: "play-circle-outline",
				keywords: `start ${module.title} ${module.subtitle}`,
				perform: () => {
					trackOption(`start-${module.id}`);
					handleDirectSessionStart({ push: true, moduleId: module.id });
				},
			});
		}

		suggested.push(
			{
				id: "quick-study",
				name: t("commandBar.actions.quickStudy"),
				subtitle: t("commandBar.actions.quickStudySubtitle"),
				section: t("commandBar.sections.suggested"),
				icon: "play-outline",
				keywords: "quick study practice session",
				perform: () => {
					trackOption("quick-study");
					handleDirectSessionStart({ push: true });
				},
			},
			{
				id: "view-progress",
				name: t("commandBar.actions.viewProgress"),
				subtitle: t("commandBar.actions.viewProgressSubtitle"),
				section: t("commandBar.sections.suggested"),
				icon: "chart-timeline-variant-shimmer",
				keywords: "progress mastery stats",
				perform: () => {
					trackOption("view-progress");
					router.push("/(protected)/(tabs)/progress");
				},
			},
		);

		const moduleActions: Action[] = moduleTags.map((tag) => {
			const module = getProethicaModuleListProps(tag, t);
			return {
				id: `module-${module.id}`,
				name: module.title,
				subtitle: `${module.cta} · ${module.typeLabel} · ${module.durationLabel}`,
				section: t("commandBar.sections.modules"),
				icon: statusIconMap[module.status],
				keywords: `${module.title} ${module.subtitle} ${module.typeLabel} ${module.cta}`,
				perform: () => {
					trackOption(`module-${module.id}`);
					if (module.status === "COMPLETE") {
						router.push("/(protected)/(tabs)/progress");
						return;
					}
					handleDirectSessionStart({ push: true, moduleId: module.id });
				},
			};
		});

		const navigate: Action[] = [
			{
				id: "modules-page",
				name: t("commandBar.actions.allModules"),
				subtitle: t("commandBar.actions.allModulesSubtitle"),
				section: t("commandBar.sections.navigate"),
				icon: "view-grid-outline",
				keywords: "modules list browse",
				perform: () => {
					trackOption("modules-page");
					router.push("/(protected)/(tabs)/modules");
				},
			},
			{
				id: "principles-page",
				name: t("commandBar.actions.principles"),
				subtitle: t("commandBar.actions.principlesSubtitle"),
				section: t("commandBar.sections.navigate"),
				icon: "book-open-page-variant-outline",
				keywords: "principles proethica ethics guide reference",
				perform: () => {
					trackOption("principles-page");
					router.push("/(protected)/(tabs)/principles");
				},
			},
			{
				id: "home",
				name: t("commandBar.actions.home"),
				section: t("commandBar.sections.navigate"),
				icon: "home-outline",
				keywords: "home dashboard",
				perform: () => {
					trackOption("home");
					router.push("/(protected)/(tabs)/home");
				},
			},
			{
				id: "leaderboard",
				name: t("commandBar.actions.leaderboard"),
				section: t("commandBar.sections.navigate"),
				icon: "trophy-outline",
				keywords: "leaderboard leaders rank",
				perform: () => {
					trackOption("leaderboard");
					router.push("/(protected)/(tabs)/leaderboard");
				},
			},
			{
				id: "settings",
				name: t("commandBar.actions.settings"),
				section: t("commandBar.sections.navigate"),
				icon: "cog-outline",
				keywords: "settings account",
				perform: () => {
					trackOption("settings");
					router.push("/account/settings");
				},
			},
		];

		return [...suggested, ...moduleActions, ...navigate];
	}, [
		moduleTags,
		t,
		router,
		handleDirectSessionStart,
		explicitCallTrackCustomEvent,
	]);
};
